import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from 'react-toastify';
import secureStorage from "../utils/secureStorage";
import { validateToken } from "../tokenValidation";
import { useNavigate } from "react-router-dom";

export default function DocumentTypeOCR(props) {
  const [documentContent, setDocumentContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [documentType, setDocumentType] = useState(null);
  const [prescriptionDetails, setPrescriptionDetails] = useState([]);
  const [encounters, setEncounters] = useState([]);
  const API_URL = process.env.REACT_APP_API_URL;
  const [allergyIntolerance, setAllergyIntolerance] = useState([]);
  const [procedureDetails, setProcedureDetails] = useState([]);
  const [medicalConditions, setMedicalConditions] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [medicineSuggestions, setMedicineSuggestions] = useState({});
  const [labTestSuggestions, setLabTestSuggestions] = useState({});
  const [labTestSearchTimeout, setLabTestSearchTimeout] = useState(null);
  const [subCentres, setSubCentres] = useState([]);
  const [selectedSubCentre, setSelectedSubCentre] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    validateToken(navigate);
  }, [navigate]);

  const fetchMedicineSuggestions = async (searchTerm, rowIndex) => {
    if (!searchTerm || searchTerm.trim().length < 2) return;
    try {
      const response = await fetch(`https://patient.curebay.com/es/api/v1/medicine-name?freeTextSearch=${searchTerm}`);
      const result = await response.json();
      const uniqueResults = Array.from(
        new Map(result.map(item => [item.medicineName, item])).values()
      );
      setMedicineSuggestions((prev) => ({
        ...prev,
        [rowIndex]: uniqueResults || []
      }));
    } catch (err) {
      console.error("Error fetching medicine suggestions", err);
    }
  };


  const fetchLabTestSuggestions = async (searchTerm, rowIndex) => {
    if (!searchTerm || searchTerm.trim().length < 2) return;
    try {
      const response = await fetch(`https://patient.curebay.com/es/api/v1/labtest?freeTextSearch=${searchTerm}`);
      const result = await response.json();
      const uniqueResults = Array.from(
        new Map(result.map(item => [item.testDisplayName, item])).values()
      );
      setLabTestSuggestions((prev) => ({
        ...prev,
        [rowIndex]: uniqueResults || []
      }));
    } catch (err) {
      console.error("Error fetching lab test suggestions", err);
    }
  };

  useEffect(() => {
    const fetchSubcentres = async () => {
      try {
        const token = secureStorage.get("authToken");
        const clinicId = secureStorage.get("clinic");
        const orgId = secureStorage.get("orgId");

        const response = await fetch(
          `${API_URL}hospital/get-subcentres?orgId=${orgId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "X-HIP-ID": clinicId,
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) throw new Error("Failed to fetch subcentres");

        const json = await response.json();
        const facilities = json.data?.facilityDetails || [];
        setSubCentres(facilities);

        // // pick default or match by existing OCR organization
        // const editingName = props.ocr?.organizationDetails?.name;
        // const defaultFacility = facilities.find(f => f.hipId === clinicId);
        // const match = editingName
        //   ? facilities.find(f => f.centerName === editingName)
        //   : defaultFacility;
        // if (match) {
        //   setSelectedSubCentre(match.centerName);
        //   setEncounters(match.encounters || []);
        // }
      } catch (err) {
        console.error(err);
      }
    };

    fetchSubcentres();
  }, [API_URL, props.ocr]);

  useEffect(() => {
    if (subCentres.length && props.ocr?.organizationDetails?.hipId) {
      const match = subCentres.find(sc => sc.hipId === props.ocr.organizationDetails.hipId);
      if (match) {
        setSelectedSubCentre(match.hipId);
        setEncounters(match.encounters || []);
        if (props.docType === "DiagnosticReport") {
          handleDiagnosticFormChange("clinicName", match.centerName);
          handleDiagnosticFormChange("clinicMobile", match.contactNumber);
          handleDiagnosticFormChange("clinicAddress", match.address);
        } else if (props.docType === "Prescription" || props.docType === "OPConsultation") {
          handlePrescriptionChange("clinicName", match.centerName);
          handlePrescriptionChange("clinicMobile", match.contactNumber);
          handlePrescriptionChange("clinicAddress", match.address);
        } else if (props.docType === "Invoice") {
          handleInvoiceFormChange("clinicName", match.centerName);
          handleInvoiceFormChange("clinicMobile", match.contactNumber);
          handleInvoiceFormChange("clinicAddress", match.address);
        }
      }
    }
  }, [subCentres, props.ocr, props.docType]);


  const handleOrgSelect = (value, formSetter) => {
    const selected = subCentres.find(c => c.hipId === value);
    if (!selected) return;
    setSelectedSubCentre(selected.hipId);
    formSetter(prev => ({
      ...prev,
      clinicName: selected.centerName,
      clinicMobile: selected.contactNumber,
      clinicAddress: selected.address
    }));
    setEncounters(selected.encounters || []);
  };

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const token = secureStorage.get("authToken");
        const clinicId = secureStorage.get("clinic");
        const orgId = secureStorage.get("orgId");

        const response = await fetch(
          `${API_URL}hospital/get-all-practitioners?orgId=${orgId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              "X-HIP-ID": clinicId,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setDoctors(data?.data || []);
        } else {
          console.error("Failed to fetch doctors", response.statusText);
        }
      } catch (error) {
        console.error("Error fetching doctors:", error);
      }
    };

    fetchDoctors();
  }, [API_URL]);


  // New state variable for edit mode
  const [editMode, setEditMode] = useState(false);
  // When the status is LINKED or OCR_PENDING and not in edit mode, fields are read-only.
  const isReadOnly =
    (props?.status === "LINKED" || props?.status === "OCR_PENDING") &&
    !editMode;

  function formatDateForInput(dateStr) {
    if (!dateStr || dateStr.trim() === "") {
      const today = new Date();
      return today.toISOString().slice(0, 10);
    }

    const parsedDate = new Date(dateStr);
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate.toISOString().slice(0, 10);
    }

    if (dateStr.includes("/")) {
      const parts = dateStr.split("/");
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
      }
    }

    if (dateStr.includes("-")) {
      const parts = dateStr.split("-");
      if (parts.length === 3 && parts[0].length !== 4) {
        return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
      }
    }

    // Assume it's already in the correct YYYY-MM-DD format
    return dateStr;
  }


  const transformGenderToCode = (gender) => {
    if (!gender) return "";
    const lower = gender.toLowerCase();
    if (lower === "male" || gender === "M") return "M";
    if (lower === "female" || gender === "F") return "F";
    return "O";
  };

  function formatDateForAPI(dateStr) {
    // Converts a date in YYYY-MM-DD format to DD-MM-YYYY
    const parts = dateStr.split("-");
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }

  const [prescriptionForm, setPrescriptionForm] = useState({
    clinicName: "",
    clinicAddress: "",
    clinicMobile: "",
    encounterDocumentType: "",
    visitDate: formatDateForInput(""),
    practitionerName: "",
    practitionerPrefix: "",
    practitionerGender: "",
    practitionerQualification: "",
    practitionerTelephone: "",
  });

  const [diagnosticForm, setDiagnosticForm] = useState({
    clinicName: "",
    clinicAddress: "",
    clinicMobile: "",
    encounterDocumentType: "",
    visitDate: formatDateForInput(""),
    practitionerName: "",
    practitionerPrefix: "",
    practitionerGender: "",
    practitionerPhoto: "",
    practitionerQualification: "",
    practitionerTelephone: "",
    patientName: "",
    patientAddress: "",
    patientDOB: "",
    patientGender: "",
    patientMaritalStatus: "",
    patientTelephone: "",
    patientPhoto: "",
  });
  const [observations, setObservations] = useState([]);

  const [invoiceForm, setInvoiceForm] = useState({
    // Practitioner Details
    practitionerName: "",
    practitionerPrefix: "",
    practitionerGender: "",
    practitionerPhoto: "",
    practitionerQualification: "",
    practitionerTelephone: "",
    // Organization
    clinicName: "",
    clinicAddress: "",
    clinicMobile: "",
    // Encounter
    encounterDocumentType: "",
    visitDate: formatDateForInput(""),
  });
  const [invoiceLineItems, setInvoiceLineItems] = useState([]);

  useEffect(() => {
    if (props.docType === "Prescription" && props.ocr) {
      setPrescriptionForm({
        clinicName: props.ocr.organizationDetails?.name || "",
        clinicAddress: props.ocr.organizationDetails?.address || "",
        clinicMobile: props.ocr.organizationDetails?.mobile || "",
        encounterDocumentType:
          props.ocr.encounterCode || props.encounterType || "",
        visitDate: formatDateForInput(props.ocr.visitDate),
        practitionerName: props.ocr.physicianInfo?.name || "",
        practitionerPrefix: props.ocr.physicianInfo?.prefix || "",
        practitionerGender: transformGenderToCode(
          props.ocr.physicianInfo?.gender
        ),
        practitionerQualification:
          props.ocr.physicianInfo?.specialization || "",
        practitionerTelephone:
          props.ocr.physicianInfo?.contact_info?.phone || "",
      });

      if (props.ocr.prescriptionsDetails) {
        setPrescriptionDetails(props.ocr.prescriptionsDetails);
      }
    }
  }, [props.docType, props.ocr, props.encounterType]);

  useEffect(() => {
    if (props.docType === "OPConsultation" && props.ocr) {
      setPrescriptionForm({
        clinicName: props.ocr.organizationDetails?.name || "",
        clinicAddress: props.ocr.organizationDetails?.address || "",
        clinicMobile: props.ocr.organizationDetails?.mobile || "",
        encounterDocumentType: props.ocr.encounterCode || props.ocr.encounterType || "",
        visitDate: formatDateForInput(props.ocr.visitDate),
        practitionerName: props.ocr.physicianInfo?.name || "",
        practitionerPrefix: props.ocr.physicianInfo?.prefix || "",
        practitionerGender: transformGenderToCode(props.ocr.physicianInfo?.gender),
        practitionerQualification: props.ocr.physicianInfo?.specialization || "",
        practitionerTelephone: props.ocr.physicianInfo?.contact_info?.phone || "",
      });

      setPrescriptionDetails(props.ocr.prescriptionsDetails || []);
      setMedicalConditions(props.ocr.medicalConditions || []);
      setProcedureDetails(props.ocr.procedureDetails || []);
      setAllergyIntolerance(props.ocr.allergyIntolerance || []);
      setObservations(props.ocr.observationsDetails || []);
    }
  }, [props.docType, props.ocr]);

  const isOPConsultationFormValid = () => {
    const {
      practitionerName,
      clinicName,
      clinicAddress,
      clinicMobile,
      encounterDocumentType,
      visitDate,
    } = prescriptionForm;

    return (
      practitionerName &&
      clinicName &&
      clinicAddress &&
      clinicMobile &&
      encounterDocumentType &&
      visitDate
    );
  };

  const handleOPConsultationAccept = async () => {
    if (!isOPConsultationFormValid()) {
      toast.error("Please fill all mandatory fields marked with *");
      return;
    }
    setSaveLoading(true);
    const updatedOCR = { ...props.ocr };

    updatedOCR.organizationDetails = {
      name: prescriptionForm.clinicName,
      address: prescriptionForm.clinicAddress,
      mobile: prescriptionForm.clinicMobile,
      hipId: selectedSubCentre
    };

    updatedOCR.healthcareProfessionalId = prescriptionForm.practitionerMongoId;

    updatedOCR.encounterCode = prescriptionForm.encounterDocumentType;
    updatedOCR.encounterType =
      encounters.find((enc) => enc.code === prescriptionForm.encounterDocumentType)?.name || "";

    updatedOCR.physicianInfo = {
      name: prescriptionForm.practitionerName,
      prefix: prescriptionForm.practitionerPrefix,
      specialization: prescriptionForm.practitionerQualification
    };
    if (!updatedOCR.documentType) {
      updatedOCR.documentType = props.docType
    }
    if (props?.abha) {
      updatedOCR.abhaNumber = props.abha;
    }

    updatedOCR.visitDate = formatDateForAPI(prescriptionForm.visitDate);
    updatedOCR.observationsDetails = observations;
    updatedOCR.allergyIntolerance = allergyIntolerance;
    updatedOCR.procedureDetails = procedureDetails;
    updatedOCR.medicalConditions = medicalConditions;
    updatedOCR.prescriptionsDetails = prescriptionDetails;
    updatedOCR.patientId = props.patientId;

    try {
      const clinicId = secureStorage.get("clinic");
      const token = secureStorage.get("authToken");
      const response = await fetch(`${API_URL}abdm-data-share/save-document`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-HIP-ID": clinicId,
        },
        body: JSON.stringify(updatedOCR),
      });
      if (response.ok) {
        const result = await response.json();
        toast.success("OP Consultation saved successfully!");
        props.onClose();
      } else {
        toast.error("Failed to save OP Consultation: " + response.statusText);
      }
    } catch (error) {
      console.error("Error sending OP Consultation:", error);
      toast.error("An error occurred while saving OP Consultation");
    } finally {
      setSaveLoading(false); // Stop loader
    }
  };

  function formatForDateTimeLocalInput(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }


  const renderEditableSection = (title, data, fields, handleChange, handleRemove, handleAdd) => (
    <div className="mb-8">
      <h3 className="font-semibold text-blue-600 mb-4">{title} <em className="text-red-600">*</em></h3>
      <table className="min-w-full border-collapse border mb-4">
        <thead>
          <tr>
            {fields.map((field) => (
              <th key={field} className="border p-2 text-[#0F766E] capitalize">
                {field.replace(/([A-Z])/g, ' $1')}
              </th>
            ))}
            {!isReadOnly && <th className="border p-2 text-[#0F766E]">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={index}>
              {fields.map((field) => (
                <td key={field} className="border p-2">
                  {field === 'outCome' ? (
                    <select
                      className="w-full"
                      value={item[field] || ''}
                      onChange={(e) => handleChange(index, field, e.target.value)}
                      disabled={isReadOnly}
                    >
                      <option value="">Select Outcome</option>
                      {[
                        "PREPARATION", "INPROGRESS", "NOTDONE", "ONHOLD", "STOPPED",
                        "COMPLETED", "ENTEREDINERROR", "UNKNOWN"
                      ].map(status => (
                        <option key={status} value={status}>
                          {status.charAt(0) + status.slice(1).toLowerCase()}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={["performedOn", "reportedDate"].includes(field) ? "datetime-local" : "text"}
                      className="w-full"
                      value={
                        ["performedOn", "reportedDate"].includes(field) && item[field]
                          ? formatForDateTimeLocalInput(item[field])
                          : item[field] || ''
                      }
                      onChange={(e) =>
                        handleChange(index, field,
                          ["performedOn", "reportedDate"].includes(field)
                            ? new Date(e.target.value).toISOString()
                            : e.target.value
                        )
                      }
                      disabled={isReadOnly || ["performedOn", "reportedDate"].includes(field)}
                    />
                  )}


                </td>
              ))}
              {!isReadOnly && (
                <td className="border p-2 text-center">
                  <button className="text-red-500" onClick={() => handleRemove(index)}>
                    Remove
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {!isReadOnly && (
        <button className="text-blue-400 font-bold mb-4" onClick={handleAdd}>
          + Add {title.slice(0, -1)}
        </button>
      )}
    </div>
  );




  // Allergy handlers
  const handleAllergyChange = (index, field, value) => {
    const updatedAllergies = [...allergyIntolerance];
    updatedAllergies[index][field] = value;
    setAllergyIntolerance(updatedAllergies);
  };

  const handleRemoveAllergy = (index) => {
    const updatedAllergies = [...allergyIntolerance];
    updatedAllergies.splice(index, 1);
    setAllergyIntolerance(updatedAllergies);
  };

  const handleAddAllergy = () => {
    setAllergyIntolerance([
      ...allergyIntolerance,
      { reportedDate: new Date().toISOString(), status: "", details: "", verificationStatus: "" },
    ]);
  };

  // Procedure handlers
  const handleProcedureChange = (index, field, value) => {
    const updatedProcedures = [...procedureDetails];
    updatedProcedures[index][field] = value;
    setProcedureDetails(updatedProcedures);
  };

  const handleRemoveProcedure = (index) => {
    const updatedProcedures = [...procedureDetails];
    updatedProcedures.splice(index, 1);
    setProcedureDetails(updatedProcedures);
  };

  const handleAddProcedure = () => {
    setProcedureDetails([
      ...procedureDetails,
      { name: "", details: "", bodySite: "", performedOn: new Date().toISOString(), outCome: "" },
    ]);
  };

  // Medical Conditions handlers
  const handleConditionChange = (index, field, value) => {
    const updatedConditions = [...medicalConditions];
    updatedConditions[index][field] = value;
    setMedicalConditions(updatedConditions);
  };

  const handleRemoveCondition = (index) => {
    const updatedConditions = [...medicalConditions];
    updatedConditions.splice(index, 1);
    setMedicalConditions(updatedConditions);
  };

  const handleAddCondition = () => {
    setMedicalConditions([
      ...medicalConditions,
      { medicalConditionName: "", reportedDate: new Date().toISOString() },
    ]);
  };


  const detectContentType = (base64) => {
    if (base64.startsWith("JVBER")) return "application/pdf";
    if (base64.startsWith("/9j/")) return "image/jpeg";
    if (base64.startsWith("iVBOR")) return "image/png";
    return "unknown";
  };

  const fetchDocumentContent = async () => {
    if (props.docNum) {
      setLoading(true);
      try {
        const token = secureStorage.get("authToken");
        if (!token) {
          console.error("Authentication token not found. Please log in again.");
          return;
        }
        const clinicId = secureStorage.get("clinic");
        const response = await fetch(
          `${API_URL}abdm-data-share/get-document?documentNumber=${props.docNum}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              "X-HIP-ID": clinicId,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          const base64Data = result.data || null;
          if (base64Data) {
            const contentType = detectContentType(base64Data);
            setDocumentContent(base64Data);
            setDocumentType(contentType);
          } else {
            console.error("No data available for the document.");
          }
        } else {
          console.error("Failed to fetch document:", response.statusText);
        }
      } catch (error) {
        console.error("Error fetching document:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchDocumentContent();
  }, []);

  useEffect(() => {
    if (props.docType === "DiagnosticReport" && props.ocr) {
      setDiagnosticForm({
        clinicName: props.ocr.organizationDetails?.name || "",
        clinicAddress: props.ocr.organizationDetails?.address || "",
        clinicMobile: props.ocr.organizationDetails?.mobile || "",
        encounterDocumentType:
          props.ocr.encounterCode || props.ocr.encounterType || "",
        visitDate: formatDateForInput(props.ocr.visitDate),
        practitionerName: props.ocr.physicianInfo?.name || "",
        practitionerPrefix: props.ocr.physicianInfo?.prefix || "",
        practitionerGender: props.ocr.physicianInfo?.gender || "",
        practitionerPhoto: props.ocr.physicianInfo?.photo || "",
        practitionerQualification:
          props.ocr.physicianInfo?.specialization || "",
        practitionerTelephone:
          props.ocr.physicianInfo?.contact_info?.phone || "",
      });
      if (props.ocr.observationsDetails) {
        setObservations(props.ocr.observationsDetails);
      }
    }
  }, [props.docType, props.ocr]);
  useEffect(() => {
    if (props.docType === "Invoice" && props.ocr) {
      setInvoiceForm({
        practitionerName: props.ocr.physicianInfo?.name || "",
        practitionerPrefix: props.ocr.physicianInfo?.prefix || "",
        practitionerGender: props.ocr.physicianInfo?.gender || "",
        practitionerQualification:
          props.ocr.physicianInfo?.qualification || "",
        practitionerTelephone: props.ocr.physicianInfo?.telephone || "",
        clinicName: props.ocr.organizationDetails?.name || "",
        clinicAddress: props.ocr.organizationDetails?.address || "",
        clinicMobile: props.ocr.organizationDetails?.mobile || "",
        encounterDocumentType:
          props.ocr.encounterCode || props.ocr.encounterType || "",
        visitDate: formatDateForInput(props.ocr.visitDate),
      });
      if (props.ocr.invoiceLineItems) {
        setInvoiceLineItems(props.ocr.invoiceLineItems);
      }
    }
  }, [props.docType, props.ocr]);

  const handleInvoiceFormChange = (field, value) => {
    setInvoiceForm({ ...invoiceForm, [field]: value });
  };

  const handleInvoiceLineItemChange = (index, field, value) => {
    const updatedItems = [...invoiceLineItems];
    updatedItems[index][field] = value;

    const quantity = parseFloat(updatedItems[index].quantity || 0);
    const mrp = parseFloat(updatedItems[index].mrp || 0);
    const discount = parseFloat(updatedItems[index].discount || 0);
    const cgst = parseFloat(updatedItems[index].cgst || 0);
    const sgst = parseFloat(updatedItems[index].sgst || 0);

    const invoiceAmount = quantity * mrp - discount;

    // GST = invoiceAmount * (cgst + sgst) / (1 + cgst + sgst)
    const totalGSTPercent = cgst + sgst;
    const gstAmount = invoiceAmount * (totalGSTPercent / (100 + totalGSTPercent));

    updatedItems[index].gstAmount = gstAmount.toFixed(2);
    updatedItems[index].invoiceAmount = invoiceAmount.toFixed(2);

    setInvoiceLineItems(updatedItems);
  };

  const grossTotal = invoiceLineItems.reduce((sum, item) => {
    const quantity = parseFloat(item.quantity || 0);
    const mrp = parseFloat(item.mrp || 0);
    return sum + quantity * mrp;
  }, 0);

  const netTotal = invoiceLineItems.reduce((sum, item) => {
    const total = parseFloat(item.invoiceAmount || 0);
    return sum + total;
  }, 0);


  const handleAddInvoiceLineItem = () => {
    setInvoiceLineItems([
      ...invoiceLineItems,
      {
        itemName: "",
        quantity: "",
        mrp: "",
        discount: "",
        cgst: "",
        sgst: "",
        gstAmount: "",
        invoiceAmount: "",
      },
    ]);
  };
  const handleRemoveInvoiceLineItem = (index) => {
    const newItems = [...invoiceLineItems];
    newItems.splice(index, 1);
    setInvoiceLineItems(newItems);
  };

  const computeTotals = () => {
    let totalNet = 0;
    let totalGross = 0;
    invoiceLineItems.forEach((item) => {
      const qty = parseFloat(item.quantity) || 0;
      const mrp = parseFloat(item.mrp) || 0;
      const discount = parseFloat(item.discount) || 0;
      const cgst = parseFloat(item.cgst) || 0;
      const sgst = parseFloat(item.sgst) || 0;
      const net = qty * mrp - discount;
      const gross = net + cgst + sgst;
      totalNet += net;
      totalGross += gross;
    });
    return { totalNet, totalGross };
  };

  const handleDiagnosticFormChange = (field, value) => {
    console.log("Name : ", field)
    console.log("Value : ", value)
    setDiagnosticForm({ ...diagnosticForm, [field]: value });
    console.log("Obj : ", diagnosticForm)
  };

  const isInvoiceFormValid = () => {
    const {
      practitionerName,
      clinicName,
      encounterDocumentType,
      visitDate
    } = invoiceForm;

    return (
      practitionerName &&
      clinicName &&
      encounterDocumentType &&
      visitDate
    );
  };

  const handleInvoiceAccept = async () => {

    if (!isInvoiceFormValid()) {
      toast.error("Please fill all mandatory fields marked with *");
      return;
    }
    setSaveLoading(true);
    const updatedOCR = { ...props.ocr };

    updatedOCR.physicianInfo = {
      ...updatedOCR.practitionerInfo,
      name: invoiceForm.practitionerName,
      prefix: invoiceForm.practitionerPrefix,
      gender: transformGenderToCode(invoiceForm.practitionerGender),
      photo: invoiceForm.practitionerPhoto,
      qualification: invoiceForm.practitionerQualification,
      telephone: invoiceForm.practitionerTelephone,
    };

    updatedOCR.organizationDetails = {
      ...updatedOCR.organizationDetails,
      name: invoiceForm.clinicName,
      address: invoiceForm.clinicAddress,
      mobile: invoiceForm.clinicMobile,
      hipId: selectedSubCentre
    };

    if (props?.abha) {
      updatedOCR.abhaNumber = props.abha;
    }
    if (!updatedOCR.documentType) {
      updatedOCR.documentType = props.docType
    }

    const selectedEncounter = encounters.find(
      (enc) => enc.code === invoiceForm.encounterDocumentType
    );
    if (selectedEncounter) {
      updatedOCR.encounterType = selectedEncounter.name;
      updatedOCR.encounterCode = selectedEncounter.code;
    } else {
      updatedOCR.encounterType = "";
      updatedOCR.encounterCode = null;
    }
    updatedOCR.visitDate = formatDateForAPI(invoiceForm.visitDate);

    updatedOCR.invoiceLineItems = invoiceLineItems;
    // const totals = computeTotals();
    // updatedOCR.totalNet = totals.totalNet;
    // updatedOCR.totalGross = totals.totalGross;
    updatedOCR.patientId = props.patientId;

    try {
      const clinicId = secureStorage.get("clinic");
      const token = secureStorage.get("authToken");
      const response = await fetch(`${API_URL}abdm-data-share/save-document`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-HIP-ID": clinicId,
        },
        body: JSON.stringify(updatedOCR),
      });
      if (response.ok) {
        const result = await response.json();
        props.onClose();
      } else {
        toast.error("Failed to save Invoice: " + response.statusText);
      }
    } catch (error) {
      console.error("Error sending Invoice:", error);
    } finally {
      setSaveLoading(false); // Stop loader
    }
  };

  const handleObservationChange = (index, field, value) => {
    const newObs = [...observations];
    newObs[index][field] = value;
    setObservations(newObs);
  };

  const handleAddObservation = () => {
    setObservations([
      ...observations,
      {
        name: "",
        value: "",
        unit: "",
        referenceRange: "",
        performerName: "",
        comment: "",
        interpretation: "",
      },
    ]);
  };

  const handleRemoveObservation = (index) => {
    const newObs = [...observations];
    newObs.splice(index, 1);
    setObservations(newObs);
  };

  const isDiagnosticFormValid = () => {
    const {
      practitionerName,
      clinicName,
      encounterDocumentType,
      visitDate
    } = diagnosticForm;

    return (
      practitionerName &&
      clinicName &&
      encounterDocumentType &&
      visitDate
    );
  };

  const handleDiagnosticAccept = async () => {

    if (!isDiagnosticFormValid()) {
      toast.error("Please fill all mandatory fields marked with *");
      return;
    }
    setSaveLoading(true);
    const updatedOCR = { ...props.ocr };

    const selectedEncounter = encounters.find(
      (enc) => enc.code === diagnosticForm.encounterDocumentType
    );
    if (selectedEncounter) {
      updatedOCR.encounterType = selectedEncounter.name;
      updatedOCR.encounterCode = selectedEncounter.code;
    } else {
      updatedOCR.encounterType = "";
      updatedOCR.encounterCode = null;
    }
    if (props?.abha) {
      updatedOCR.abhaNumber = props.abha;
    }
    if (!updatedOCR.documentType) {
      updatedOCR.documentType = props.docType
    }

    updatedOCR.healthcareProfessionalId = diagnosticForm.practitionerMongoId;

    updatedOCR.organizationDetails = {
      ...updatedOCR.organizationDetails,
      name: diagnosticForm.clinicName,
      address: diagnosticForm.clinicAddress,
      mobile: diagnosticForm.clinicMobile,
      hipId: selectedSubCentre
    };
    updatedOCR.visitDate = formatDateForAPI(diagnosticForm.visitDate);

    updatedOCR.physicianInfo = {
      ...updatedOCR.physicianInfo,
      name: diagnosticForm.practitionerName,
      prefix: diagnosticForm.practitionerPrefix,
      gender: transformGenderToCode(diagnosticForm.practitionerGender),
      photo: diagnosticForm.practitionerPhoto,
      specialization: diagnosticForm.practitionerQualification,
      contact_info: {
        ...(updatedOCR.physicianInfo?.contact_info || {}),
        phone: diagnosticForm.practitionerTelephone,
      },
    };

    updatedOCR.ocrExtractedData = {
      ...updatedOCR.ocrExtractedData,
    };

    updatedOCR.observationsDetails = observations;

    updatedOCR.patientId = props.patientId;

    try {
      const clinicId = secureStorage.get("clinic");
      const token = secureStorage.get("authToken");
      const response = await fetch(`${API_URL}abdm-data-share/save-document`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-HIP-ID": clinicId,
        },
        body: JSON.stringify(updatedOCR),
      });
      if (response.ok) {
        const result = await response.json();
        props.onClose();
      } else {
        console.error("Failed to save Diagnostic Report:", response.statusText);
      }
    } catch (error) {
      console.error("Error sending Diagnostic Report:", error);
    } finally {
      setSaveLoading(false); // Stop loader
    }
  };

  const handlePrescriptionChange = (field, value) => {
    setPrescriptionForm({ ...prescriptionForm, [field]: value });
  };

  const handlePrescriptionDetailChange = (index, field, value) => {
    const newDetails = [...prescriptionDetails];
    newDetails[index][field] = value;
    setPrescriptionDetails(newDetails);
  };

  const handleAddPrescriptionDetail = () => {
    setPrescriptionDetails([
      ...prescriptionDetails,
      {
        drugName: "",
        dosage: "",
        frequency: "",
        route: "",
        duration: "",
        instructions: "",
        prescriptionDate: "",
      },
    ]);
  };

  const handleRemovePrescriptionDetail = (index) => {
    const newDetails = [...prescriptionDetails];
    newDetails.splice(index, 1);
    setPrescriptionDetails(newDetails);
  };

  const isPrescriptionFormValid = () => {
    const {
      practitionerName,
      clinicName,
      clinicAddress,
      clinicMobile,
      encounterDocumentType,
      visitDate,
    } = prescriptionForm;

    return (
      practitionerName &&
      clinicName &&
      clinicAddress &&
      clinicMobile &&
      encounterDocumentType &&
      visitDate
    );
  };


  const handlePrescriptionAccept = async () => {

    if (!isPrescriptionFormValid()) {
      toast.error("Please fill all mandatory fields marked with *");
      return;
    }
    setSaveLoading(true);
    const updatedOCR = { ...props.ocr };

    updatedOCR.organizationDetails = {
      ...updatedOCR.organizationDetails,
      name: prescriptionForm.clinicName,
      address: prescriptionForm.clinicAddress,
      mobile: prescriptionForm.clinicMobile,
      hipId: selectedSubCentre
    };

    updatedOCR.healthcareProfessionalId = prescriptionForm.practitionerMongoId;

    updatedOCR.visitDate = formatDateForAPI(prescriptionForm.visitDate);

    if (props?.abha) {
      updatedOCR.abhaNumber = props.abha;
    }
    if (!updatedOCR.documentType) {
      updatedOCR.documentType = props.docType
    }
    updatedOCR.physicianInfo = {
      ...updatedOCR.physicianInfo,
      name: prescriptionForm.practitionerName,
      prefix: prescriptionForm.practitionerPrefix,
      gender: prescriptionForm.practitionerGender,
      qualification: prescriptionForm.practitionerQualification,
      contact_info: {
        ...(updatedOCR.physicianInfo?.contact_info || {}),
        phone: prescriptionForm.practitionerTelephone,
      },
    };

    updatedOCR.prescriptionsDetails = prescriptionDetails;

    const selectedEncounter = encounters.find(
      (enc) => enc.code === prescriptionForm.encounterDocumentType
    );
    if (selectedEncounter) {
      updatedOCR.encounterType = selectedEncounter.name;
      updatedOCR.encounterCode = selectedEncounter.code;
    } else {
      updatedOCR.encounterType = "";
      updatedOCR.encounterCode = null;
    }
    updatedOCR.patientId = props.patientId;


    try {
      const token = secureStorage.get("authToken");
      const clinicId = secureStorage.get("clinic");
      const response = await fetch(`${API_URL}abdm-data-share/save-document`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-HIP-ID": clinicId,
        },
        body: JSON.stringify(updatedOCR),
      });
      if (response.ok) {
        const result = await response.json();
        props.onClose();
      } else {
        toast.error("Failed to save prescription:", response.statusText);
      }
    } catch (error) {
      console.error("Error sending prescription:", error);
    } finally {
      setSaveLoading(false); // Stop loader
    }
  };

  // useEffect(() => {
  //   const fetchEncounters = async () => {
  //     try {
  //       const token = secureStorage.get("authToken");
  //       const response = await fetch(
  //         `${API_URL}abdm-data-share/get-available-encounters`,
  //         {
  //           method: "GET",
  //           headers: {
  //             "Content-Type": "application/json",
  //             Authorization: `Bearer ${token}`,
  //           },
  //         }
  //       );

  //       if (!response.ok) {
  //         throw new Error(`HTTP error! Status: ${response.status}`);
  //       }

  //       const data = await response.json();
  //       setEncounters(data?.data || []);
  //     } catch (error) {
  //       console.error("Error fetching Encounters:", error);
  //     }
  //   };

  //   fetchEncounters();
  // }, [API_URL]);
  return (
    <>
      <div className="w-full bg-white border rounded-lg p-4 shadow mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 text-sm md:text-base">
          <p><span className="font-semibold text-gray-700">Patient Name:</span> {props.name || "N/A"}</p>
          <p><span className="font-semibold text-gray-700">ABHA Address:</span> {props.abhaAddress || "N/A"}</p>
          <p><span className="font-semibold text-gray-700">Patient ID:</span> {props.patientId || "N/A"}</p>
          <p><span className="font-semibold text-gray-700">ABHA Number:</span> {props.abha || "N/A"}</p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 p-4 bg-gray-50">

        {/* Document Viewer */}
        {documentContent && (
          <div
            className={`${props.docType === "HealthDocumentRecord" ? "w-full" : "w-full md:w-1/2"
              } border rounded-lg p-2 bg-white shadow`}
          >
            <h2 className="text-lg font-semibold mb-2 text-teal-600">Document</h2>
            <div className="w-full h-full overflow-hidden relative">
              {loading ? (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                  <div className="w-16 h-16 border-4 border-t-teal-600 border-gray-300 rounded-full animate-spin"></div>
                </div>
              ) : documentType === "application/pdf" ? (
                <iframe
                  src={`data:application/pdf;base64,${documentContent}`}
                  title="PDF Document"
                  className="w-full rounded-md border"
                  style={{ height: "600px" }}
                />
              ) : (
                <img
                  src={`data:image/jpeg;base64,${documentContent}`}
                  alt="Document"
                  className="w-full rounded-md object-contain"
                />
              )}
            </div>
          </div>
        )}

        {props.docType === "DiagnosticReport" && (
          <div className={`${documentContent ? "w-full md:w-2/3" : "w-full"} border rounded-lg p-4 bg-white shadow`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-teal-600">
                Diagnostic Report
              </h2>
              {isReadOnly && (
                <button
                  className="bg-green-500 text-white px-4 py-2 w-32 rounded"
                  onClick={() => setEditMode(true)}
                >
                  Edit
                </button>
              )}
            </div>

            {/* Practitioner Section */}
            <h3 className="font-semibold text-blue-600 mb-4">Practitioner</h3>

            <div className="col-span-2">
              <select
                className="w-full border p-2"
                onChange={(e) => {
                  const selected = doctors.find(d => d.id === e.target.value);
                  if (selected) {
                    setDiagnosticForm(prev => ({
                      ...prev,
                      practitionerName: selected.name,
                      practitionerMongoId: selected.id
                    }));
                  }
                }}
                disabled={isReadOnly}
              >
                <option value="">Select Practitioner</option>
                {doctors.map((doc) => (
                  <option key={doc.id} value={doc.id}>
                    {doc.name}
                  </option>
                ))}
              </select>
            </div>

            {(diagnosticForm.practitionerName) && (
              <>
                <div className="bg-gray-50 border rounded-lg p-3 mt-2">
                  {diagnosticForm.practitionerName && (
                    <p className="text-gray-800"><strong>Practitioner Name:</strong> {diagnosticForm.practitionerName}</p>
                  )}
                </div>
              </>
            )}


            {/* Organization Section */}
            <h3 className="font-semibold text-blue-600 mt-4 mb-4">Organization</h3>
            <div className="col-span-2">
              <select
                className="w-full border p-2"
                value={selectedSubCentre}
                onChange={(e) => handleOrgSelect(e.target.value, setDiagnosticForm)}
                disabled={isReadOnly}
              >
                <option value="">Select Organization</option>
                {subCentres.map(c => (
                  <option key={c.hipId} value={c.hipId}>
                    {c.centerName}
                  </option>
                ))}
              </select>
            </div>

            {(diagnosticForm.clinicName || diagnosticForm.clinicMobile || diagnosticForm.clinicAddress) && (
              <>
                <div className="bg-gray-50 border rounded-lg p-3 mt-2">
                  {diagnosticForm.clinicName && (
                    <p className="text-gray-800"><strong>Clinic Name:</strong> {diagnosticForm.clinicName}</p>
                  )}
                  {diagnosticForm.clinicMobile && (
                    <p className="text-gray-800"><strong>Clinic Mobile:</strong> {diagnosticForm.clinicMobile}</p>
                  )}
                  {diagnosticForm.clinicAddress && (
                    <p className="text-gray-800"><strong>Clinic Address:</strong> {diagnosticForm.clinicAddress}</p>
                  )}
                </div>
              </>
            )}


            {/* Encounter Section */}
            <h3 className="font-semibold text-blue-600 mt-4 mb-4">Encounter</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block mb-1 font-medium text-gray-700">Encounter Type <em className="text-red-600">*</em></label>
                <select
                  className="w-full border p-2"
                  value={diagnosticForm.encounterDocumentType}
                  onChange={(e) => handleDiagnosticFormChange("encounterDocumentType", e.target.value)}
                  disabled={isReadOnly}
                >
                  <option value="">Select Encounter Type</option>
                  {encounters.map((enc, index) => (
                    <option key={index} value={enc.code}>
                      {enc.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-1 font-medium text-gray-700">Visit Date <em className="text-red-600">*</em></label>
                <input
                  type="date"
                  className="w-full border p-2"
                  value={diagnosticForm.visitDate}
                  onChange={(e) => handleDiagnosticFormChange("visitDate", e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
            </div>

            {/* Observations Table */}
            <h3 className="font-semibold text-blue-600 mb-1">Observations (Lab Tests) <em className="text-red-600">*</em></h3>
            <p className="text-sm text-gray-500">
              Type in <strong>Lab Test Name</strong> to search and select from suggestions.
            </p>
            <table className="min-w-full border-collapse border mb-4">
              <thead>
                <tr>
                  <th className="border p-2 text-[#0F766E] w-96">Name</th>
                  <th className="border p-2 text-[#0F766E]">Value</th>
                  <th className="border p-2 text-[#0F766E]">Reference Range</th>
                  <th className="border p-2 text-[#0F766E]">Unit</th>
                  <th className="border p-2 text-[#0F766E]">Note</th>
                  <th className="border p-2 text-[#0F766E]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {observations.map((obs, index) => (
                  <tr key={index}>
                    <td className="border p-2">
                      <div className="relative z-50">
                        <input
                          type="text"
                          className="w-full"
                          value={obs.name}
                          onChange={(e) => {
                            handleObservationChange(index, "name", e.target.value);
                            if (labTestSearchTimeout) clearTimeout(labTestSearchTimeout);
                            const timeout = setTimeout(() => {
                              fetchLabTestSuggestions(e.target.value, index);
                            }, 500);
                            setLabTestSearchTimeout(timeout);
                          }}
                          disabled={isReadOnly}
                        />
                        {!isReadOnly && labTestSuggestions[index]?.length > 0 && (
                          <ul className="absolute z-50 bg-white border w-full max-h-60 overflow-y-auto shadow-md rounded-md">
                            {labTestSuggestions[index].map((suggestion, i) => (
                              <li
                                key={i}
                                className="p-2 hover:bg-gray-100 cursor-pointer"
                                onClick={() => {
                                  handleObservationChange(index, "name", suggestion.testDisplayName);
                                  setLabTestSuggestions((prev) => ({
                                    ...prev,
                                    [index]: [],
                                  }));
                                }}
                              >
                                {suggestion.testDisplayName}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </td>
                    <td className="border p-2">
                      <input
                        type="text"
                        className="w-full"
                        value={obs.value}
                        onChange={(e) => handleObservationChange(index, "value", e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border p-2">
                      <input
                        type="text"
                        className="w-full"
                        value={obs.referenceRange}
                        onChange={(e) => handleObservationChange(index, "referenceRange", e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border p-2">
                      {obs.unit !== 'other' ? (
                        <select
                          className="w-full"
                          value={obs.unit}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleObservationChange(index, "unit", value);
                            if (value === 'other') {
                              handleObservationChange(index, "customUnit", ""); // Init custom field
                            }
                          }}
                          disabled={isReadOnly}
                        >
                          <option value="">Select Unit</option>
                          <option value="mg/dL">mg/dL</option>
                          <option value="g/dL">g/dL</option>
                          <option value="mmol/L">mmol/L</option>
                          <option value="ng/mL">ng/mL</option>
                          <option value="µIU/mL">µIU/mL</option>
                          <option value="%">%</option>
                          <option value="mm/hr">mm/hr</option>
                          <option value="10^3/µL">10^3/µL</option>
                          <option value="10^9/L">10^9/L</option>
                          <option value="other">Other</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          className="w-full"
                          placeholder="Enter custom unit"
                          value={obs.customUnit || ""}
                          onChange={(e) => handleObservationChange(index, "unit", e.target.value)}
                          disabled={isReadOnly}
                        />
                      )}

                    </td>
                    <td className="border p-2">
                      <input
                        type="text"
                        className="w-full"
                        value={obs.comment}
                        onChange={(e) => handleObservationChange(index, "comment", e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border p-2 text-center">
                      {!isReadOnly && (
                        <button
                          className="text-red-500"
                          onClick={() => handleRemoveObservation(index)}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {!isReadOnly && (
              <button
                className="text-blue-500 font-bold mb-4"
                onClick={handleAddObservation}
              >
                + Add Observation
              </button>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between items-start mt-4">
              <div className="flex flex-col pr-3">
                <p className="text-black-700 text-sm mb-1 font-semibold">Note :</p>
                <p className="text-red-600 text-sm mb-1">- All fields with * are compulsory to fill.</p>
                <p className="text-gray-600 text-sm mb-1">- Quantity can have units such as #tablets, #strips, #bottle in cases of syrup.</p>
                <p className="text-gray-600 text-sm mb-1">- Upon Save, document will be linked to the patient's ABHA address.</p>
              </div>

              {!isReadOnly && (
                <div className="flex gap-2">
                  <button
                    className="bg-blue-500 text-white px-4 py-2 rounded"
                    onClick={handleDiagnosticAccept}
                    disabled={saveLoading}
                  >
                    {saveLoading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      "Save"
                    )}
                  </button>
                  <button
                    className="bg-red-500 text-white px-4 py-2 rounded"
                    onClick={() => {
                      setEditMode(false);
                      props.onClose();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {props.docType === "Prescription" && (
          <div className={`${documentContent ? "w-full md:w-2/3" : "w-full"} border rounded-lg p-4 bg-white shadow`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-teal-600">Prescription Report</h2>
              {isReadOnly && (
                <button
                  className="bg-green-500 text-white px-4 py-2 w-32 rounded"
                  onClick={() => setEditMode(true)}
                >
                  Edit
                </button>
              )}
            </div>

            {/* Practitioner Section */}
            <h3 className="font-semibold text-blue-600 mb-4">Practitioner</h3>

            <div className="col-span-2">
              <select
                className="w-full border p-2"
                onChange={(e) => {
                  const selected = doctors.find(d => d.id === e.target.value);
                  if (selected) {
                    setPrescriptionForm(prev => ({
                      ...prev,
                      practitionerName: selected.name,
                      practitionerMongoId: selected.id
                    }));
                  }
                }}
                disabled={isReadOnly}
              >
                <option value="">Select Practitioner</option>
                {doctors.map((doc) => (
                  <option key={doc.id} value={doc.id}>
                    {doc.name}
                  </option>
                ))}
              </select>
            </div>

            {(prescriptionForm.practitionerName) && (
              <>
                <div className="bg-gray-50 border rounded-lg p-3 mt-2">
                  {prescriptionForm.practitionerName && (
                    <p className="text-gray-800"><strong>Practitioner Name:</strong> {prescriptionForm.practitionerName}</p>
                  )}
                </div>
              </>
            )}


            {/* Organization Section */}
            <h3 className="font-semibold text-blue-600 mt-4 mb-4">Organization</h3>
            <select
              className="w-full border p-2"
              value={selectedSubCentre}
              onChange={(e) => handleOrgSelect(e.target.value, setPrescriptionForm)}
              disabled={isReadOnly}
            >
              <option value="">Select Organization</option>
              {subCentres.map(c => (
                <option key={c.hipId} value={c.hipId}>
                  {c.centerName}
                </option>
              ))}
            </select>

            {(prescriptionForm.clinicName || prescriptionForm.clinicMobile || prescriptionForm.clinicAddress) && (
              <>
                <div className="bg-gray-50 border rounded-lg p-3 mt-2">
                  {prescriptionForm.clinicName && (
                    <p className="text-gray-800"><strong>Clinic Name:</strong> {prescriptionForm.clinicName}</p>
                  )}
                  {prescriptionForm.clinicMobile && (
                    <p className="text-gray-800"><strong>Clinic Mobile:</strong> {prescriptionForm.clinicMobile}</p>
                  )}
                  {prescriptionForm.clinicAddress && (
                    <p className="text-gray-800"><strong>Clinic Address:</strong> {prescriptionForm.clinicAddress}</p>
                  )}
                </div>
              </>
            )}

            {/* Encounter Section */}
            <h3 className="font-semibold text-blue-600 mt-4 mb-4">Encounter</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block mb-1 font-medium text-gray-700">Encounter Type <em className="text-red-600">*</em></label>
                <select
                  className="w-full border p-2"
                  value={prescriptionForm.encounterDocumentType}
                  onChange={(e) => handlePrescriptionChange("encounterDocumentType", e.target.value)}
                  disabled={isReadOnly}
                >
                  <option value="">Select Encounter Type</option>
                  {encounters.map((enc, index) => (
                    <option key={index} value={enc.code}>
                      {enc.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-1 font-medium text-gray-700">Visit Date <em className="text-red-600">*</em></label>
                <input
                  type="date"
                  className="w-full border p-2"
                  value={prescriptionForm.visitDate}
                  onChange={(e) => handlePrescriptionChange("visitDate", e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
            </div>

            {/* Medication Section */}
            <h3 className="font-semibold text-blue-600 mb-1">Medication Request <em className="text-red-600">*</em></h3>
            <p className="text-sm text-gray-500 ">
              Type in <strong>Medicine Name</strong> to search and select from suggestions.
            </p>
            <table className="min-w-full border-collapse border mb-4">
              <thead>
                <tr>
                  <th className="border p-2 text-[#0F766E] w-96">
                    Medicine Name
                  </th>
                  <th className="border p-2 text-[#0F766E]">
                    Dosage
                    <div className="text-xs text-gray-500 italic">(e.g. 500mg)</div>
                  </th>
                  <th className="border p-2 text-[#0F766E]">
                    Frequency
                    <div className="text-xs text-gray-500 italic">times/day</div>
                  </th>
                  <th className="border p-2 text-[#0F766E]">
                    Route
                    <div className="text-xs text-gray-500 italic">(e.g. Oral)</div>
                  </th>
                  <th className="border p-2 text-[#0F766E]">
                    Duration
                    <div className="text-xs text-gray-500 italic">in Days</div>
                    <div className="text-xs text-gray-500 italic">(e.g. 5)</div>
                  </th>
                  <th className="border p-2 text-[#0F766E]">
                    Administration Method
                    <div className="text-xs text-gray-500 italic">(e.g. After Meal)</div>
                  </th>
                  <th className="border p-2 text-[#0F766E]">
                    Special Instructions / Notes
                  </th>
                  {!isReadOnly && (
                    <th className="border p-2 text-[#0F766E]">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {prescriptionDetails.map((presc, index) => (
                  <tr key={index}>
                    {['drugName', 'dosage', 'frequency', 'route', 'duration', 'method', 'instructions'].map((field) => (
                      <td key={field} className="border p-2">
                        {field === 'drugName' ? (
                          <div className="relative z-50">
                            <input
                              type="text"
                              className="w-full"
                              placeholder="Search or type medicine"
                              value={presc[field]}
                              onChange={(e) => {
                                handlePrescriptionDetailChange(index, field, e.target.value);
                                if (searchTimeout) clearTimeout(searchTimeout);
                                const timeout = setTimeout(() => {
                                  fetchMedicineSuggestions(e.target.value, index);
                                }, 500);
                                setSearchTimeout(timeout);
                              }}
                              disabled={isReadOnly}
                            />
                            {!isReadOnly && medicineSuggestions[index]?.length > 0 && (
                              <ul
                                className="absolute z-50 bg-white border w-full max-h-60 overflow-y-auto shadow-md rounded-md"
                                style={{ top: "100%", left: 0 }}
                              >
                                {medicineSuggestions[index].map((suggestion, i) => (
                                  <li
                                    key={i}
                                    className="p-2 hover:bg-gray-100 cursor-pointer"
                                    onClick={() => {
                                      handlePrescriptionDetailChange(index, field, suggestion.medicineName);
                                      setMedicineSuggestions((prev) => ({
                                        ...prev,
                                        [index]: [],
                                      }));
                                    }}
                                  >
                                    {suggestion.medicineName}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ) : field === 'frequency' ? (
                          <select
                            className="w-full"
                            value={presc[field]}
                            onChange={(e) => handlePrescriptionDetailChange(index, field, e.target.value)}
                            disabled={isReadOnly}
                          >
                            <option value="">Select Frequency</option>
                            <option value="1">Once a day</option>
                            <option value="2">Twice a day</option>
                            <option value="3">Thrice a day</option>
                          </select>
                        ) : field === 'route' ? (
                          <select
                            className="w-full"
                            value={presc[field]}
                            onChange={(e) => handlePrescriptionDetailChange(index, field, e.target.value)}
                            disabled={isReadOnly}
                          >
                            <option value="">Select Route</option>
                            <option value="Oral">Oral</option>
                            <option value="Topical">Topical</option>
                            <option value="Intravenous">Intravenous (IV)</option>
                            <option value="Intramuscular">Intramuscular (IM)</option>
                            <option value="Subcutaneous">Subcutaneous</option>
                            <option value="Nasal">Nasal</option>
                            <option value="Inhalation">Inhalation</option>
                          </select>
                        ) : field === 'method' ? (
                          <select
                            className="w-full"
                            value={presc[field]}
                            onChange={(e) => handlePrescriptionDetailChange(index, field, e.target.value)}
                            disabled={isReadOnly}
                          >
                            <option value="">Select Method</option>
                            <option value="Before Meal">Before Meal</option>
                            <option value="After Meal">After Meal</option>
                            <option value="With Meal">With Meal</option>
                            <option value="Empty Stomach">Empty Stomach</option>
                            <option value="At Bedtime">At Bedtime</option>
                          </select>
                        ) : (
                          <input
                            type={['duration'].includes(field) ? 'number' : 'text'}
                            className="w-full"
                            placeholder={field}
                            value={presc[field]}
                            onChange={(e) => handlePrescriptionDetailChange(index, field, e.target.value)}
                            disabled={isReadOnly}
                          />
                        )}
                      </td>
                    ))}

                    {!isReadOnly && (
                      <td className="border p-2 text-center">
                        <button
                          type="button"
                          className="text-red-500"
                          onClick={() => handleRemovePrescriptionDetail(index)}
                        >
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {!isReadOnly && (
              <button
                onClick={handleAddPrescriptionDetail}
                className="text-blue-500 font-bold mb-4"
              >
                + Add Medicine
              </button>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between items-start mt-4">
              <div className="flex flex-col pr-3">
                <p className="text-black-700 text-sm mb-1 font-semibold">Note :</p>
                <p className="text-red-600 text-sm mb-1">- All fields with * are compulsory to fill.</p>
                <p className="text-gray-600 text-sm mb-1">- Quantity can have units such as #tablets, #strips, #bottle in cases of syrup.</p>
                <p className="text-gray-600 text-sm mb-1">- Upon Save, document will be linked to the patient's ABHA address.</p>
              </div>

              {!isReadOnly && (
                <div className="flex gap-2">
                  <button
                    className="bg-blue-500 text-white px-4 py-2 rounded"
                    onClick={handlePrescriptionAccept}
                    disabled={saveLoading}
                  >
                    {saveLoading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      "Save"
                    )}
                  </button>
                  <button
                    className="bg-red-500 text-white px-4 py-2 rounded"
                    onClick={() => {
                      setEditMode(false);
                      props.onClose();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {props.docType === "Invoice" && (
          <div className={`${documentContent ? "w-full md:w-2/3" : "w-full"} border rounded-lg p-4 bg-white shadow`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-teal-600">
                Invoice Record
              </h2>

              {isReadOnly && (
                <button
                  className="bg-green-500 text-white px-4 py-2 w-32 rounded"
                  onClick={() => setEditMode(true)}
                >
                  Edit
                </button>
              )}
            </div>

            {/* Practitioner Section */}
            <h3 className="font-semibold text-blue-600 mb-4">Practitioner</h3>

            <div className="col-span-2">
              <select
                className="w-full border p-2"
                onChange={(e) => {
                  const selected = doctors.find(d => d.id === e.target.value);
                  if (selected) {
                    setInvoiceForm(prev => ({
                      ...prev,
                      practitionerName: selected.name
                    }));
                  }
                }}
                disabled={isReadOnly}
              >
                <option value="">Select Practitioner</option>
                {doctors.map((doc) => (
                  <option key={doc.id} value={doc.id}>
                    {doc.name}
                  </option>
                ))}
              </select>
            </div>

            {(invoiceForm.practitionerName) && (
              <>
                <div className="bg-gray-50 border rounded-lg p-3 mt-2">
                  {invoiceForm.practitionerName && (
                    <p className="text-gray-800"><strong>Practitioner Name:</strong> {invoiceForm.practitionerName}</p>
                  )}
                </div>
              </>
            )}


            {/* Organization Section */}
            <h3 className="font-semibold text-blue-600 mt-4 mb-4">Organization</h3>
            <select
              className="w-full border p-2"
              value={selectedSubCentre}
              onChange={(e) => handleOrgSelect(e.target.value, setInvoiceForm)}
              disabled={isReadOnly}
            >
              <option value="">Select Organization</option>
              {subCentres.map(c => (
                <option key={c.hipId} value={c.hipId}>
                  {c.centerName}
                </option>
              ))}
            </select>

            {(invoiceForm.clinicName || invoiceForm.clinicMobile || invoiceForm.clinicAddress) && (
              <>
                <div className="bg-gray-50 border rounded-lg p-3 mt-2">
                  {invoiceForm.clinicName && (
                    <p className="text-gray-800"><strong>Clinic Name:</strong> {invoiceForm.clinicName}</p>
                  )}
                  {invoiceForm.clinicMobile && (
                    <p className="text-gray-800"><strong>Clinic Mobile:</strong> {invoiceForm.clinicMobile}</p>
                  )}
                  {invoiceForm.clinicAddress && (
                    <p className="text-gray-800"><strong>Clinic Address:</strong> {invoiceForm.clinicAddress}</p>
                  )}
                </div>
              </>
            )}

            {/* Encounter Section */}
            <h3 className="font-semibold text-blue-600 mt-4 mb-4">Encounter</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block mb-1 font-medium text-gray-700">Encounter Type <em className="text-red-600">*</em></label>
                <select
                  className="w-full border p-2"
                  value={invoiceForm.encounterDocumentType}
                  onChange={(e) => handleInvoiceFormChange("encounterDocumentType", e.target.value)}
                  disabled={isReadOnly}
                >
                  <option value="">Select Encounter Type</option>
                  {encounters.map((enc, index) => (
                    <option key={index} value={enc.code}>
                      {enc.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-1 font-medium text-gray-700">Visit Date <em className="text-red-600">*</em></label>
                <input
                  type="date"
                  className="w-full border p-2"
                  value={invoiceForm.visitDate}
                  onChange={(e) => handleInvoiceFormChange("visitDate", e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
            </div>

            {/* Invoice Line Items */}
            <h3 className="font-semibold text-blue-600 mb-4">Invoice Line Items <em className="text-red-600">*</em></h3>
            <table className="min-w-full border-collapse border mb-4">
              <thead>
                <tr>
                  <th className="border p-2 text-[#0F766E] w-64">Item Name</th>
                  <th className="border p-2 text-[#0F766E] w-32">MRP (INR)</th>
                  <th className="border p-2 text-[#0F766E]">Quantity</th>
                  <th className="border p-2 text-[#0F766E]">Discount (INR)</th>
                  <th className="border p-2 text-[#0F766E]">CGST (%)</th>
                  <th className="border p-2 text-[#0F766E]">SGST (%)</th>
                  <th className="border p-2 text-[#0F766E]">GST (INR)</th>
                  <th className="border p-2 text-[#0F766E]">Invoice Amount</th>
                  <th className="border p-2 text-[#0F766E]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoiceLineItems.map((item, index) => (
                  <tr key={index}>
                    <td className="border p-2">
                      <div className="relative z-50">
                        <input
                          type="text"
                          className="w-full"
                          value={item.itemName}
                          onChange={(e) => {
                            handleInvoiceLineItemChange(index, "itemName", e.target.value);
                            if (searchTimeout) clearTimeout(searchTimeout);
                            const timeout = setTimeout(() => {
                              fetchMedicineSuggestions(e.target.value, index);
                            }, 500);
                            setSearchTimeout(timeout);
                          }}
                          disabled={isReadOnly}
                        />
                        {!isReadOnly && medicineSuggestions[index]?.length > 0 && (
                          <ul className="absolute z-50 bg-white border w-full max-h-60 overflow-y-auto shadow-md rounded-md">
                            {medicineSuggestions[index].map((suggestion, i) => (
                              <li
                                key={i}
                                className="p-2 hover:bg-gray-100 cursor-pointer"
                                onClick={() => {
                                  handleInvoiceLineItemChange(index, "itemName", suggestion.medicineName);
                                  handleInvoiceLineItemChange(index, "mrp", suggestion.medicineRate); // Populate rate
                                  setMedicineSuggestions((prev) => ({
                                    ...prev,
                                    [index]: [],
                                  }));
                                }}
                              >
                                <span className="font-medium">{suggestion.medicineName}</span>
                                {suggestion.medicineRate ? (
                                  <span className="text-gray-500 ml-2 text-sm">₹{suggestion.medicineRate}</span>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </td>


                    <td className="border p-2">
                      <input
                        type="number"
                        className="w-full"
                        value={item.mrp}
                        onChange={(e) => handleInvoiceLineItemChange(index, "mrp", e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border p-2">
                      <input
                        type="number"
                        className="w-full"
                        value={item.quantity}
                        onChange={(e) => handleInvoiceLineItemChange(index, "quantity", e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border p-2">
                      <input
                        type="number"
                        className="w-full"
                        value={item.discount}
                        onChange={(e) => handleInvoiceLineItemChange(index, "discount", e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border p-2">
                      <input
                        type="number"
                        className="w-full"
                        value={item.cgst}
                        onChange={(e) => handleInvoiceLineItemChange(index, "cgst", e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border p-2">
                      <input
                        type="number"
                        className="w-full"
                        value={item.sgst}
                        onChange={(e) => handleInvoiceLineItemChange(index, "sgst", e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border p-2">
                      {/* <input
                        type="number"
                        className="w-full bg-gray-100"
                        value={item.gstAmount}
                        readOnly
                      /> */}
                      {item.gstAmount}
                    </td>
                    <td className="border p-2">
                      {/* <input
                        type="number"
                        className="w-full bg-gray-100"
                        value={item.invoiceAmount}
                        readOnly
                      /> */}
                      {item.invoiceAmount}
                    </td>
                    <td className="border p-2 text-center">
                      {!isReadOnly && (
                        <button
                          className="text-red-500"
                          onClick={() => handleRemoveInvoiceLineItem(index)}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {!isReadOnly && (
              <button
                onClick={handleAddInvoiceLineItem}
                className="text-blue-500 font-bold mb-1"
              >
                + Add Line Item
              </button>
            )}

            {/* Totals Table Lookalike */}
            <div className="flex justify-end">
              <table className="border border-gray-300 text-right">
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-semibold text-gray-700">
                      Gross Total
                    </td>
                    <td className="border border-gray-300 px-4 py-2 font-bold">
                      {grossTotal.toFixed(2)}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-semibold text-gray-700">
                      Net Total
                    </td>
                    <td className="border border-gray-300 px-4 py-2 font-bold">
                      {netTotal.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-start mt-6">
              <div className="flex flex-col pr-3">
                <p className="text-black-700 text-sm mb-1 font-semibold">Note :</p>
                <p className="text-red-600 text-sm mb-1">- All fields with * are compulsory to fill.</p>
                <p className="text-gray-600 text-sm mb-1">- Quantity can have units such as #tablets, #strips, #bottle in cases of syrup.</p>
                <p className="text-gray-600 text-sm mb-1">- MRP is the printed MRP on the item, inclusive of GST. It corresponds to one unit of reported Quantity.</p>
                <p className="text-gray-600 text-sm mb-1">- Discount is the total discount on the item for all quantity invoiced.</p>
                <p className="text-gray-600 text-sm mb-1">- Gross Total is the total MRP value of items sold (inclusive of GST).</p>
                <p className="text-gray-600 text-sm mb-1">- Net Total is the total invoice value payable, net of discount (inclusive of GST).</p>
                <p className="text-gray-600 text-sm mb-1">- Upon Save, document will be linked to the patient's ABHA address.</p>
              </div>

              {!isReadOnly && (
                <div className="flex gap-2">
                  <button
                    className="bg-blue-500 text-white px-4 py-2 rounded"
                    onClick={handleInvoiceAccept}
                    disabled={saveLoading}
                  >
                    {saveLoading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      "Save"
                    )}
                  </button>
                  <button
                    className="bg-red-500 text-white px-4 py-2 rounded"
                    onClick={() => {
                      setEditMode(false);
                      props.onClose();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {props.docType === "OPConsultation" && (
          <div className={`${documentContent ? "w-full md:w-2/3" : "w-full"} border rounded-lg p-4 bg-white shadow`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-teal-600">OP Consultation Report</h2>
              {isReadOnly && (
                <button
                  className="bg-green-500 text-white px-4 py-2 w-32 rounded"
                  onClick={() => setEditMode(true)}
                >
                  Edit
                </button>
              )}
            </div>

            {/* Practitioner Section */}
            <h3 className="font-semibold text-blue-600 mb-4">Practitioner</h3>

            <div className="col-span-2">
              <select
                className="w-full border p-2"
                onChange={(e) => {
                  const selected = doctors.find(d => d.id === e.target.value);
                  if (selected) {
                    setPrescriptionForm(prev => ({
                      ...prev,
                      practitionerName: selected.name,
                      practitionerMongoId: selected.id
                    }));
                  }
                }}
                disabled={isReadOnly}
              >
                <option value="">Select Practitioner</option>
                {doctors.map((doc) => (
                  <option key={doc.id} value={doc.id}>
                    {doc.name}
                  </option>
                ))}
              </select>
            </div>

            {(prescriptionForm.practitionerName) && (
              <>
                <div className="bg-gray-50 border rounded-lg p-3 mt-2">
                  {prescriptionForm.practitionerName && (
                    <p className="text-gray-800"><strong>Practitioner Name:</strong> {prescriptionForm.practitionerName}</p>
                  )}
                </div>
              </>
            )}


            {/* Organization Section */}
            <h3 className="font-semibold text-blue-600 mt-4 mb-4">Organization</h3>
            <select
              className="w-full border p-2"
              value={selectedSubCentre}
              onChange={(e) => handleOrgSelect(e.target.value, setPrescriptionForm)}
              disabled={isReadOnly}
            >
              <option value="">Select Organization</option>
              {subCentres.map(c => (
                <option key={c.hipId} value={c.hipId}>
                  {c.centerName}
                </option>
              ))}
            </select>

            {(prescriptionForm.clinicName || prescriptionForm.clinicMobile || prescriptionForm.clinicAddress) && (
              <>
                <div className="bg-gray-50 border rounded-lg p-3 mt-2">
                  {prescriptionForm.clinicName && (
                    <p className="text-gray-800"><strong>Clinic Name:</strong> {prescriptionForm.clinicName}</p>
                  )}
                  {prescriptionForm.clinicMobile && (
                    <p className="text-gray-800"><strong>Clinic Mobile:</strong> {prescriptionForm.clinicMobile}</p>
                  )}
                  {prescriptionForm.clinicAddress && (
                    <p className="text-gray-800"><strong>Clinic Address:</strong> {prescriptionForm.clinicAddress}</p>
                  )}
                </div>
              </>
            )}


            {/* Encounter Section */}
            <h3 className="font-semibold text-blue-600 mt-4 mb-4">Encounter</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block mb-1 font-medium text-gray-700">Encounter Type <em className="text-red-600">*</em></label>
                <select
                  className="w-full border p-2 cursor-pointer"
                  value={prescriptionForm.encounterDocumentType || ''}
                  onChange={(e) => handlePrescriptionChange("encounterDocumentType", e.target.value)}
                  disabled={isReadOnly}
                >
                  <option value="">Select Encounter Type</option>
                  {encounters.map((enc) => (
                    <option key={enc.code} value={enc.code}>{enc.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-1 font-medium text-gray-700">Visit Date <em className="text-red-600">*</em></label>
                <input
                  type="date"
                  className="w-full border p-2"
                  value={prescriptionForm.visitDate || ''}
                  onChange={(e) => handlePrescriptionChange("visitDate", e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
            </div>
            {/* Medication Request */}
            <h3 className="font-semibold text-blue-600 mb-4">Medication Request <em className="text-red-600">*</em></h3>
            <p className="text-sm text-gray-500 ">
              Type in <strong>Medicine Name</strong> to search and select from suggestions.
            </p>
            <table className="min-w-full border-collapse border mb-4">
              <thead>
                <tr>
                  <th className="border p-2 text-[#0F766E] w-96">
                    Medicine Name
                  </th>
                  <th className="border p-2 text-[#0F766E]">
                    Dosage
                    <div className="text-xs text-gray-500 italic">(e.g. 500mg)</div>
                  </th>
                  <th className="border p-2 text-[#0F766E]">
                    Frequency
                    <div className="text-xs text-gray-500 italic">times/day</div>
                  </th>
                  <th className="border p-2 text-[#0F766E]">
                    Route
                    <div className="text-xs text-gray-500 italic">(e.g. Oral)</div>
                  </th>
                  <th className="border p-2 text-[#0F766E]">
                    Duration
                    <div className="text-xs text-gray-500 italic">in Days</div>
                    <div className="text-xs text-gray-500 italic">(e.g. 5)</div>
                  </th>
                  <th className="border p-2 text-[#0F766E]">
                    Administration Method
                    <div className="text-xs text-gray-500 italic">(e.g. After Meal)</div>
                  </th>
                  <th className="border p-2 text-[#0F766E]">
                    Special Instructions / Notes
                  </th>
                  {!isReadOnly && (
                    <th className="border p-2 text-[#0F766E]">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {prescriptionDetails.map((presc, index) => (
                  <tr key={index}>
                    {['drugName', 'dosage', 'frequency', 'route', 'duration', 'method', 'instructions'].map((field) => (
                      <td key={field} className="border p-2">
                        {field === 'drugName' ? (
                          <div className="relative z-50">
                            <input
                              type="text"
                              className="w-full"
                              placeholder="Search or type medicine"
                              value={presc[field]}
                              onChange={(e) => {
                                handlePrescriptionDetailChange(index, field, e.target.value);
                                if (searchTimeout) clearTimeout(searchTimeout);
                                const timeout = setTimeout(() => {
                                  fetchMedicineSuggestions(e.target.value, index);
                                }, 500);
                                setSearchTimeout(timeout);
                              }}
                              disabled={isReadOnly}
                            />
                            {!isReadOnly && medicineSuggestions[index]?.length > 0 && (
                              <ul
                                className="absolute z-50 bg-white border w-full max-h-60 overflow-y-auto shadow-md rounded-md"
                                style={{ top: "100%", left: 0 }}
                              >
                                {medicineSuggestions[index].map((suggestion, i) => (
                                  <li
                                    key={i}
                                    className="p-2 hover:bg-gray-100 cursor-pointer"
                                    onClick={() => {
                                      handlePrescriptionDetailChange(index, field, suggestion.medicineName);
                                      setMedicineSuggestions((prev) => ({
                                        ...prev,
                                        [index]: [],
                                      }));
                                    }}
                                  >
                                    {suggestion.medicineName}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ) : field === 'frequency' ? (
                          <select
                            className="w-full"
                            value={presc[field]}
                            onChange={(e) => handlePrescriptionDetailChange(index, field, e.target.value)}
                            disabled={isReadOnly}
                          >
                            <option value="">Select Frequency</option>
                            <option value="1">Once a day</option>
                            <option value="2">Twice a day</option>
                            <option value="3">Thrice a day</option>
                          </select>
                        ) : field === 'route' ? (
                          <select
                            className="w-full"
                            value={presc[field]}
                            onChange={(e) => handlePrescriptionDetailChange(index, field, e.target.value)}
                            disabled={isReadOnly}
                          >
                            <option value="">Select Route</option>
                            <option value="Oral">Oral</option>
                            <option value="Topical">Topical</option>
                            <option value="Intravenous">Intravenous (IV)</option>
                            <option value="Intramuscular">Intramuscular (IM)</option>
                            <option value="Subcutaneous">Subcutaneous</option>
                            <option value="Nasal">Nasal</option>
                            <option value="Inhalation">Inhalation</option>
                          </select>
                        ) : field === 'method' ? (
                          <select
                            className="w-full"
                            value={presc[field]}
                            onChange={(e) => handlePrescriptionDetailChange(index, field, e.target.value)}
                            disabled={isReadOnly}
                          >
                            <option value="">Select Method</option>
                            <option value="Before Meal">Before Meal</option>
                            <option value="After Meal">After Meal</option>
                            <option value="With Meal">With Meal</option>
                            <option value="Empty Stomach">Empty Stomach</option>
                            <option value="At Bedtime">At Bedtime</option>
                          </select>
                        ) : (
                          <input
                            type={['duration'].includes(field) ? 'number' : 'text'}
                            className="w-full"
                            placeholder={field}
                            value={presc[field]}
                            onChange={(e) => handlePrescriptionDetailChange(index, field, e.target.value)}
                            disabled={isReadOnly}
                          />
                        )}
                      </td>
                    ))}

                    {!isReadOnly && (
                      <td className="border p-2 text-center">
                        <button
                          type="button"
                          className="text-red-500"
                          onClick={() => handleRemovePrescriptionDetail(index)}
                        >
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {!isReadOnly && (
              <button
                type="button"
                onClick={handleAddPrescriptionDetail}
                className="text-blue-400 font-bold mb-4"
              >
                + Add Medicine
              </button>
            )}


            {/* Observations Table */}
            {/* {renderEditableSection("Observations", observations, ['name', 'value', 'unit', 'referenceRange', 'performerName', 'comment'], handleObservationChange, handleRemoveObservation, handleAddObservation)} */}

            <h3 className="font-semibold text-blue-600 mb-1">Observations (Lab Tests) <em className="text-red-600">*</em></h3>
            <p className="text-sm text-gray-500">
              Type in <strong>Lab Test Name</strong> to search and select from suggestions.
            </p>
            <table className="min-w-full border-collapse border mb-4">
              <thead>
                <tr>
                  <th className="border p-2 text-[#0F766E] w-64">Name</th>
                  <th className="border p-2 text-[#0F766E]">Value</th>
                  <th className="border p-2 text-[#0F766E]">Reference Range</th>
                  <th className="border p-2 text-[#0F766E]">Unit</th>
                  <th className="border p-2 text-[#0F766E]">Performer Name</th>
                  <th className="border p-2 text-[#0F766E]">Note</th>
                  {!isReadOnly && <th className="border p-2 text-[#0F766E]">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {observations.map((obs, index) => (
                  <tr key={index}>
                    <td className="border p-2">
                      <div className="relative z-50">
                        <input
                          type="text"
                          className="w-full"
                          value={obs.name}
                          onChange={(e) => {
                            handleObservationChange(index, "name", e.target.value);
                            if (labTestSearchTimeout) clearTimeout(labTestSearchTimeout);
                            const timeout = setTimeout(() => {
                              fetchLabTestSuggestions(e.target.value, index);
                            }, 500);
                            setLabTestSearchTimeout(timeout);
                          }}
                          disabled={isReadOnly}
                        />
                        {!isReadOnly && labTestSuggestions[index]?.length > 0 && (
                          <ul className="absolute z-50 bg-white border w-full max-h-60 overflow-y-auto shadow-md rounded-md">
                            {labTestSuggestions[index].map((suggestion, i) => (
                              <li
                                key={i}
                                className="p-2 hover:bg-gray-100 cursor-pointer"
                                onClick={() => {
                                  handleObservationChange(index, "name", suggestion.testDisplayName);
                                  setLabTestSuggestions((prev) => ({
                                    ...prev,
                                    [index]: [],
                                  }));
                                }}
                              >
                                {suggestion.testDisplayName}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </td>
                    <td className="border p-2">
                      <input
                        type="text"
                        className="w-full"
                        value={obs.value}
                        onChange={(e) => handleObservationChange(index, "value", e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border p-2">
                      <input
                        type="text"
                        className="w-full"
                        value={obs.referenceRange}
                        onChange={(e) => handleObservationChange(index, "referenceRange", e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border p-2">
                      {obs.unit !== 'other' ? (
                        <select
                          className="w-full"
                          value={obs.unit}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleObservationChange(index, "unit", value);
                            if (value === 'other') {
                              handleObservationChange(index, "customUnit", ""); // Init custom field
                            }
                          }}
                          disabled={isReadOnly}
                        >
                          <option value="">Select Unit</option>
                          <option value="mg/dL">mg/dL</option>
                          <option value="g/dL">g/dL</option>
                          <option value="mmol/L">mmol/L</option>
                          <option value="ng/mL">ng/mL</option>
                          <option value="µIU/mL">µIU/mL</option>
                          <option value="%">%</option>
                          <option value="mm/hr">mm/hr</option>
                          <option value="10^3/µL">10^3/µL</option>
                          <option value="10^9/L">10^9/L</option>
                          <option value="other">Other</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          className="w-full"
                          placeholder="Enter custom unit"
                          value={obs.customUnit || ""}
                          onChange={(e) => handleObservationChange(index, "unit", e.target.value)}
                          disabled={isReadOnly}
                        />
                      )}
                    </td>
                    <td className="border p-2">
                      <input
                        type="text"
                        className="w-full"
                        value={obs.performerName}
                        onChange={(e) => handleObservationChange(index, "performerName", e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border p-2">
                      <input
                        type="text"
                        className="w-full"
                        value={obs.comment}
                        onChange={(e) => handleObservationChange(index, "comment", e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    {!isReadOnly && (
                      <td className="border p-2 text-center">
                        <button
                          type="button"
                          className="text-red-500"
                          onClick={() => handleRemoveObservation(index)}
                        >
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {!isReadOnly && (
              <button
                type="button"
                className="text-blue-400 font-bold mb-4"
                onClick={handleAddObservation}
              >
                + Add Observation
              </button>
            )}

            <h3 className="font-semibold text-blue-600 mb-4">Allergy Intolerance <em className="text-red-600">*</em></h3>
            <table className="min-w-full border-collapse border mb-4">
              <thead>
                <tr>
                  <th className="border p-2 text-[#0F766E]">Details</th>
                  <th className="border p-2 text-[#0F766E]">Reported Date</th>
                  <th className="border p-2 text-[#0F766E]">Status</th>
                  <th className="border p-2 text-[#0F766E]">Verification Status</th>
                  {!isReadOnly && <th className="border p-2 text-[#0F766E]">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {allergyIntolerance.map((item, index) => (
                  <tr key={index}>
                    <td className="border p-2">
                      <input
                        type="text"
                        className="w-full"
                        value={item.details}
                        onChange={(e) => handleAllergyChange(index, 'details', e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border p-2">
                      <input
                        type="datetime-local"
                        className="w-full"
                        value={formatForDateTimeLocalInput(item.reportedDate)}
                        onChange={(e) => handleAllergyChange(index, 'reportedDate', new Date(e.target.value).toISOString())}
                        disabled
                      />
                    </td>
                    <td className="border p-2">
                      <select
                        className="w-full"
                        value={item.status}
                        onChange={(e) => handleAllergyChange(index, 'status', e.target.value)}
                        disabled={isReadOnly}
                      >
                        <option value="">Select Status</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Resolved">Resolved</option>
                      </select>
                    </td>
                    <td className="border p-2">
                      <select
                        className="w-full"
                        value={item.verificationStatus}
                        onChange={(e) => handleAllergyChange(index, 'verificationStatus', e.target.value)}
                        disabled={isReadOnly}
                      >
                        <option value="">Select Verification</option>
                        <option value="Confirmed">Confirmed</option>
                        <option value="Unconfirmed">Unconfirmed</option>
                        <option value="Refuted">Refuted</option>
                        <option value="Entered in Error">Entered in Error</option>
                      </select>
                    </td>

                    {!isReadOnly && (
                      <td className="border p-2 text-center">
                        <button className="text-red-500" onClick={() => handleRemoveAllergy(index)}>Remove</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {!isReadOnly && (
              <button className="text-blue-400 font-bold mb-4" onClick={handleAddAllergy}>
                + Add Allergy
              </button>
            )}


            {/* Allergy Intolerance */}
            {/* {renderEditableSection("Allergy Intolerance", allergyIntolerance, ['details', 'reportedDate', 'status', 'verificationStatus'], handleAllergyChange, handleRemoveAllergy, handleAddAllergy)} */}

            {/* Procedure Details */}
            {renderEditableSection("Procedure Details", procedureDetails, ['name', 'details', 'bodySite', 'performedOn', 'outCome'], handleProcedureChange, handleRemoveProcedure, handleAddProcedure)}

            {/* Medical Conditions */}
            {renderEditableSection("Medical Conditions", medicalConditions, ['medicalConditionName', 'reportedDate'], handleConditionChange, handleRemoveCondition, handleAddCondition)}

            <div className="flex justify-between items-start mt-4">
              <div className="flex flex-col pr-3">
                <p className="text-black-700 text-sm mb-1 font-semibold">Note :</p>
                <p className="text-red-600 text-sm mb-1">- All fields with * are compulsory to fill.</p>
                <p className="text-gray-600 text-sm mb-1">- Quantity can have units such as #tablets, #strips, #bottle in cases of syrup.</p>
                <p className="text-gray-600 text-sm mb-1">- Upon Save, document will be linked to the patient's ABHA address.</p>
              </div>

              {!isReadOnly && (
                <div className="flex gap-2">
                  <button className="bg-blue-500 text-white px-4 py-2 rounded" onClick={handleOPConsultationAccept}
                    disabled={saveLoading}
                  >
                    {saveLoading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      "Save"
                    )}
                  </button>
                  <button className="bg-red-500 text-white px-4 py-2 rounded" onClick={() => { setEditMode(false); props.onClose(); }}>Cancel</button>
                </div>
              )}
            </div>
          </div>
        )}


        <ToastContainer position="top-right" autoClose={3000} />

      </div>

    </>
  );
}
